import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Instant } from '@js-joda/core';
import { useFetch, usePromise } from '@rad/hooks';
import Link from '@rad/components/utility/link';
import { parseInstantN } from '@rad/utility/parsers';
import { getPublicTour, getPublicTourSummary, getTour } from '../../../../common/network/tour';
import { getParcelView } from '../../../../common/network/property';
import { districtUrls } from '../../../../common/routes/urls/district';
import { tourThemeOf } from '../../../../theme/tour';
import { useTourTheme } from '../../../../hooks/useTourTheme';
import { previewUrl, useTourPreview } from '../../../../hooks/useTourPreview';
const Loading = (props) => _jsxs("div", { className: "container py-5", "aria-busy": "true", children: [_jsx("div", { className: "skeleton-block skeleton-lg skeleton-w-100 mb-3", style: { 'height': 220 } }), _jsx("div", { className: "skeleton-block skeleton-line skeleton-w-50 mb-2" }), _jsx("div", { className: "skeleton-block skeleton-line skeleton-w-75" })] });
const Unavailable = (props) => _jsxs("div", { className: "container py-5 text-center", children: [_jsx("p", { className: "text-muted", children: props.message }), _jsxs(Link, { className: "btn tour-home-link-cta", href: props.href, children: [_jsx("i", { className: "bi bi-arrow-left me-2", "aria-hidden": "true" }), props.label] })] });
const backToTour = (tourId, previewing) => ({ href: previewUrl(districtUrls.toursDetail.getURL({ id: tourId }), previewing), label: 'Back to the tour' });
// The tour is not live, or not this community's. A tour that is there gets named, and its page is
// one tap away; one that is not sends people to the community's tours.
const TourUnavailable = (props) => {
    const [summaryPromise] = useFetch(getPublicTourSummary(props.id));
    const summary = usePromise(summaryPromise);
    if (summary === undefined)
        return _jsx(Loading, {});
    if (summary === null) {
        return _jsx(Unavailable, { message: "This tour is not available.", href: districtUrls.tours.getURL(), label: "See all tours" });
    }
    const message = summary.status === 'archived'
        ? `${summary.title} is no longer available.`
        : `${summary.title} is not yet available.`;
    return _jsx(Unavailable, Object.assign({ message: message }, backToTour(props.id, props.previewing)));
};
// Why a visitor could not open this home yet, for a preview to say so; null when they could.
const closedToVisitors = (tour, stop, now) => {
    const starts = parseInstantN(tour.starts);
    return tour.status !== 'active' ? `this tour is ${tour.statusDisplay.toLowerCase()}`
        : starts === null || now.isBefore(starts) ? 'the tour has not begun'
            : stop.status !== 'published' ? 'this home has not been announced'
                : null;
};
const PreviewBanner = (props) => _jsxs("div", { className: "tour-preview-banner", children: [_jsx("i", { className: "bi bi-eye", "aria-hidden": "true" }), _jsxs("span", { children: ["Preview \u2014 ", props.reason, ", so visitors cannot open this home yet."] })] });
const ViewLoader = (props) => {
    const [viewPromise] = useFetch(getParcelView(props.stop.propertyId));
    const view = usePromise(viewPromise);
    if (view === undefined)
        return _jsx(Loading, {});
    if (view === null)
        return _jsx(Unavailable, Object.assign({ message: "This home's details are not available." }, backToTour(props.tour.id, props.previewing)));
    const reason = props.previewing ? closedToVisitors(props.tour, props.stop, Instant.now()) : null;
    return _jsxs(_Fragment, { children: [reason !== null && _jsx(PreviewBanner, { reason: reason }), props.children({ tour: props.tour, stop: props.stop, index: props.index, view, previewing: props.previewing })] });
};
// Every screen about one home on a tour reads the tour first, to find the home and its place on the
// route, then the parcel the home sits on. The page wears the tour's theme throughout, as the tour's
// own page does. Whoever manages the tour can preview its homes before visitors can: that reads the
// tour through the endpoint that checks their permission, and anyone else gets the public tour.
const TourHomeLoader = (props) => {
    const previewing = useTourPreview();
    const [tourPromise] = useFetch(previewing ? getTour(false, props.id) : getPublicTour(props.id));
    const tour = usePromise(tourPromise);
    const theme = useMemo(() => tour ? tourThemeOf(tour.themeSettings) : null, [tour]);
    useTourTheme(theme);
    if (tour === undefined)
        return _jsx(Loading, {});
    if (tour === null)
        return _jsx(TourUnavailable, { id: props.id, previewing: previewing });
    const index = tour.stops.findIndex(s => s.id === props.stopId);
    const stop = tour.stops[index];
    // A house not yet announced keeps its place on the route but has nothing to show.
    if (stop === undefined || stop.title === '') {
        return _jsx(Unavailable, Object.assign({ message: "This home has not been announced yet." }, backToTour(tour.id, previewing)));
    }
    // The server names the property only once the tour has started.
    const propertyId = stop.propertyId;
    if (propertyId === null) {
        return _jsx(Unavailable, Object.assign({ message: "This home opens when the tour begins." }, backToTour(tour.id, previewing)));
    }
    return _jsx(ViewLoader, { tour: tour, stop: Object.assign(Object.assign({}, stop), { propertyId }), index: index, previewing: previewing, children: props.children }, stop.id);
};
export default TourHomeLoader;
